/**
 * Camera-movement arrow overlay — data + geometry, ported from Unity.
 *
 * Unity composites these arrows over the shot image at DISPLAY time (they are never baked into the
 * render: movement is absent from TimelineCacheHasher's tile hash, so changing it does not bust a
 * thumbnail). The web app receives the clean render, so it has to draw the same composite itself.
 *
 * Source of truth, verbatim:
 *   Assets/Scripts/PrefabControllers/ShotMovementArrowsOverlayController.cs
 *     - ShotMovementArrowType + its used-area fractions  -> USED below
 *     - shotMovementArrowSetupDefinitionDictionary       -> SETUP below (22 entries)
 *     - UpdateArrowPositions()                           -> computeMovementArrowLayout below
 *   Assets/Scripts/Document/DocumentShotMovement.cs
 *     - shotMovementTypeWithOverlayGraphicsList          -> SETUP's key set (pinned by a test)
 *
 * No React here on purpose: the geometry is pure and testable without a DOM.
 */
import arcSprite from '@/assets/shotMovementArrows/arw_arc.png?inline';
import dollyInSprite from '@/assets/shotMovementArrows/arw_dollyin.png?inline';
import dollyOutSprite from '@/assets/shotMovementArrows/arw_dollyout.png?inline';
import moveBackwardSprite from '@/assets/shotMovementArrows/arw_movebackward.png?inline';
import moveForwardSprite from '@/assets/shotMovementArrows/arw_moveforward.png?inline';
import panSprite from '@/assets/shotMovementArrows/arw_pan.png?inline';
import pedestalSprite from '@/assets/shotMovementArrows/arw_pedestal.png?inline';
import tiltSprite from '@/assets/shotMovementArrows/arw_tilt.png?inline';
import truckSprite from '@/assets/shotMovementArrows/arw_truck.png?inline';
import whipPanSprite from '@/assets/shotMovementArrows/arw_whip.png?inline';

/** The 10 sprites the live setup table actually references. Unity's enum has 16; Direction,
 *  MoveSideways, Rotate, TurnAround, TurnCorner and Zoom appear only in commented-out entries,
 *  so their art is deliberately NOT copied into the web bundle. */
export type ArrowType =
  | 'Arc' | 'DollyIn' | 'DollyOut' | 'MoveBackward' | 'MoveForward'
  | 'Pan' | 'Pedestal' | 'Tilt' | 'Truck' | 'WhipPan';

/** `?inline` data URIs, matching ShotTypeBadge — the embedded host loads over file://, where a
 *  relative asset URL is one more thing that can resolve wrong. Sprites are downscaled to 512px
 *  (Unity ships 2048px; nothing here renders above ~200px). */
const SPRITE: Record<ArrowType, string> = {
  Arc: arcSprite,
  DollyIn: dollyInSprite,
  DollyOut: dollyOutSprite,
  MoveBackward: moveBackwardSprite,
  MoveForward: moveForwardSprite,
  Pan: panSprite,
  Pedestal: pedestalSprite,
  Tilt: tiltSprite,
  Truck: truckSprite,
  WhipPan: whipPanSprite,
};

/** Fraction of each (square) sprite canvas the glyph actually covers. The layout uses it to cancel
 *  the transparent margin so an edge-anchored arrow sits against the frame edge, not its own
 *  padding. Numbers are the controller's own literals. */
const USED: Record<ArrowType, { w: number; h: number }> = {
  Arc: { w: 1916 / 2048, h: 1010 / 2048 },
  DollyIn: { w: 1580 / 2048, h: 1580 / 2048 },
  DollyOut: { w: 1420 / 2048, h: 1420 / 2048 },
  MoveBackward: { w: 644 / 2048, h: 1316 / 2048 },
  MoveForward: { w: 1194 / 2048, h: 1702 / 2048 },
  Pan: { w: 1918 / 2048, h: 242 / 2048 },
  Pedestal: { w: 352 / 2048, h: 1992 / 2048 },
  Tilt: { w: 314 / 2048, h: 1866 / 2048 },
  Truck: { w: 1894 / 2048, h: 194 / 2048 },
  // 316, per the size dictionary. The enum's own trailing comment says 315 — the dictionary is
  // what UpdateArrowPositions reads, so it wins.
  WhipPan: { w: 1664 / 2048, h: 316 / 2048 },
};

type ArrowPosition =
  | 'BottomLeft' | 'TopLeft' | 'TopRight' | 'BottomRight'
  | 'TopMiddle' | 'BottomMiddle' | 'LeftMiddle' | 'RightMiddle';

/** Unity's BasedOnWidth / BasedOnHeight / SmallestDimension / LargestDimension. */
type ArrowScaling = 'width' | 'height' | 'smallest' | 'largest';

/** Unity's None / Clockwise090 / Clockwise180 / Clockwise270, in CSS degrees. */
type ArrowRotation = 0 | 90 | 180 | 270;

interface ArrowDef {
  type: ArrowType;
  pos: ArrowPosition;
  scaling: ArrowScaling;
  scale: number;
  padX: number;
  padY: number;
  rot: ArrowRotation;
  flipX: boolean;
  flipY: boolean;
}

/**
 * One entry per movement type that has overlay art, keyed by the NATIVE enum name — the same key
 * Unity looks up (its entries also carry an inner `shotMovementType` field, which the lookup never
 * reads and which is copy/pasted wrong on ZoomOutShot; it is not reproduced here).
 */
export const MOVEMENT_ARROW_SETUP: Record<string, ArrowDef[]> = {
  ZoomInShot: [
    { type: 'DollyIn', pos: 'BottomLeft', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 270, flipX: false, flipY: false },
    { type: 'DollyIn', pos: 'TopLeft', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 0, flipX: false, flipY: false },
    { type: 'DollyIn', pos: 'TopRight', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 90, flipX: false, flipY: false },
    { type: 'DollyIn', pos: 'BottomRight', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 180, flipX: false, flipY: false },
  ],
  ZoomOutShot: [
    { type: 'DollyOut', pos: 'BottomLeft', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 270, flipX: false, flipY: false },
    { type: 'DollyOut', pos: 'TopLeft', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 0, flipX: false, flipY: false },
    { type: 'DollyOut', pos: 'TopRight', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 90, flipX: false, flipY: false },
    { type: 'DollyOut', pos: 'BottomRight', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 180, flipX: false, flipY: false },
  ],
  PanLeftShot: [
    { type: 'Pan', pos: 'BottomMiddle', scaling: 'width', scale: 0.8, padX: 0.05, padY: 0.05, rot: 0, flipX: true, flipY: false },
  ],
  PanRightShot: [
    { type: 'Pan', pos: 'BottomMiddle', scaling: 'width', scale: 0.8, padX: 0.05, padY: 0.05, rot: 0, flipX: false, flipY: false },
  ],
  TiltUpShot: [
    { type: 'Tilt', pos: 'LeftMiddle', scaling: 'height', scale: 0.8, padX: 0.05, padY: 0.05, rot: 0, flipX: true, flipY: false },
    { type: 'Tilt', pos: 'RightMiddle', scaling: 'height', scale: 0.8, padX: 0.05, padY: 0.05, rot: 0, flipX: false, flipY: false },
  ],
  TiltDownShot: [
    { type: 'Tilt', pos: 'LeftMiddle', scaling: 'height', scale: 0.8, padX: 0.05, padY: 0.05, rot: 0, flipX: true, flipY: true },
    { type: 'Tilt', pos: 'RightMiddle', scaling: 'height', scale: 0.8, padX: 0.05, padY: 0.05, rot: 0, flipX: false, flipY: true },
  ],
  DollyInShot: [
    { type: 'MoveBackward', pos: 'BottomMiddle', scaling: 'smallest', scale: 0.5, padX: 0, padY: 0.02, rot: 0, flipX: false, flipY: false },
  ],
  DollyOutShot: [
    { type: 'MoveForward', pos: 'BottomMiddle', scaling: 'smallest', scale: 0.5, padX: 0, padY: 0.02, rot: 0, flipX: false, flipY: false },
  ],
  TruckLeftShot: [
    { type: 'Truck', pos: 'TopMiddle', scaling: 'width', scale: 0.8, padX: 0.05, padY: 0.02, rot: 0, flipX: true, flipY: false },
    { type: 'Truck', pos: 'BottomMiddle', scaling: 'width', scale: 0.8, padX: 0.05, padY: 0.02, rot: 0, flipX: true, flipY: false },
  ],
  TruckRightShot: [
    { type: 'Truck', pos: 'TopMiddle', scaling: 'width', scale: 0.8, padX: 0.05, padY: 0.02, rot: 0, flipX: false, flipY: false },
    { type: 'Truck', pos: 'BottomMiddle', scaling: 'width', scale: 0.8, padX: 0.05, padY: 0.02, rot: 0, flipX: false, flipY: false },
  ],
  DollyZoomInShot: [
    { type: 'DollyIn', pos: 'BottomLeft', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 270, flipX: false, flipY: false },
    { type: 'DollyIn', pos: 'TopLeft', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 0, flipX: false, flipY: false },
    { type: 'DollyIn', pos: 'TopRight', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 90, flipX: false, flipY: false },
    { type: 'DollyIn', pos: 'BottomRight', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 180, flipX: false, flipY: false },
    { type: 'MoveForward', pos: 'BottomMiddle', scaling: 'smallest', scale: 0.5, padX: 0, padY: 0.02, rot: 0, flipX: false, flipY: false },
  ],
  DollyZoomOutShot: [
    { type: 'DollyOut', pos: 'BottomLeft', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 270, flipX: false, flipY: false },
    { type: 'DollyOut', pos: 'TopLeft', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 0, flipX: false, flipY: false },
    { type: 'DollyOut', pos: 'TopRight', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 90, flipX: false, flipY: false },
    { type: 'DollyOut', pos: 'BottomRight', scaling: 'smallest', scale: 0.2, padX: 0.02, padY: 0.02, rot: 180, flipX: false, flipY: false },
    { type: 'MoveBackward', pos: 'BottomMiddle', scaling: 'smallest', scale: 0.5, padX: 0, padY: 0.02, rot: 0, flipX: false, flipY: false },
  ],
  PedestalUpShot: [
    { type: 'Pedestal', pos: 'LeftMiddle', scaling: 'height', scale: 0.8, padX: 0.02, padY: 0.05, rot: 0, flipX: true, flipY: false },
    { type: 'Pedestal', pos: 'RightMiddle', scaling: 'height', scale: 0.8, padX: 0.02, padY: 0.05, rot: 0, flipX: false, flipY: false },
  ],
  PedestalDownShot: [
    { type: 'Pedestal', pos: 'LeftMiddle', scaling: 'height', scale: 0.8, padX: 0.02, padY: 0.05, rot: 0, flipX: true, flipY: true },
    { type: 'Pedestal', pos: 'RightMiddle', scaling: 'height', scale: 0.8, padX: 0.02, padY: 0.05, rot: 0, flipX: false, flipY: true },
  ],
  WhipPanLeftShot: [
    { type: 'WhipPan', pos: 'BottomMiddle', scaling: 'width', scale: 0.6, padX: 0.05, padY: 0.02, rot: 0, flipX: true, flipY: false },
  ],
  WhipPanRightShot: [
    { type: 'WhipPan', pos: 'BottomMiddle', scaling: 'width', scale: 0.6, padX: 0.05, padY: 0.02, rot: 0, flipX: false, flipY: false },
  ],
  WhipTiltUpShot: [
    { type: 'WhipPan', pos: 'LeftMiddle', scaling: 'height', scale: 0.6, padX: 0.02, padY: 0.05, rot: 270, flipX: false, flipY: true },
    { type: 'WhipPan', pos: 'RightMiddle', scaling: 'height', scale: 0.6, padX: 0.02, padY: 0.05, rot: 270, flipX: false, flipY: false },
  ],
  WhipTiltDownShot: [
    { type: 'WhipPan', pos: 'LeftMiddle', scaling: 'height', scale: 0.6, padX: 0.02, padY: 0.05, rot: 270, flipX: true, flipY: true },
    { type: 'WhipPan', pos: 'RightMiddle', scaling: 'height', scale: 0.6, padX: 0.02, padY: 0.05, rot: 270, flipX: true, flipY: false },
  ],
  ArcLeftShot: [
    { type: 'Arc', pos: 'BottomMiddle', scaling: 'width', scale: 0.4, padX: 0.05, padY: 0.05, rot: 0, flipX: true, flipY: false },
  ],
  ArcRightShot: [
    { type: 'Arc', pos: 'BottomMiddle', scaling: 'width', scale: 0.4, padX: 0.05, padY: 0.05, rot: 0, flipX: false, flipY: false },
  ],
  RollLeftShot: [
    { type: 'Arc', pos: 'LeftMiddle', scaling: 'height', scale: 0.5, padX: 0.05, padY: 0.05, rot: 90, flipX: false, flipY: false },
    { type: 'Arc', pos: 'RightMiddle', scaling: 'height', scale: 0.5, padX: 0.05, padY: 0.05, rot: 270, flipX: false, flipY: false },
  ],
  RollRightShot: [
    { type: 'Arc', pos: 'LeftMiddle', scaling: 'height', scale: 0.5, padX: 0.05, padY: 0.05, rot: 90, flipX: true, flipY: false },
    { type: 'Arc', pos: 'RightMiddle', scaling: 'height', scale: 0.5, padX: 0.05, padY: 0.05, rot: 270, flipX: true, flipY: false },
  ],
};

/** One arrow, in percentages of the SHOT FRAME (not of the 16:9 box the frame is letterboxed in). */
export interface ArrowLayout {
  sprite: string;
  leftPct: number;
  topPct: number;
  widthPct: number;
  heightPct: number;
  /** CSS `transform`, rotation then flip — the same R∘S order Unity composes. */
  transform: string;
}

/**
 * Port of UpdateArrowPositions(). `ar` is the shot/document aspect ratio (width / height), the same
 * value Unity feeds SetShotMovementType. Returns [] for every movement without overlay art
 * (including 'Unspecified' and the un-directed parents like 'PanShot'), which is the caller's
 * signal to render nothing at all.
 */
export function computeMovementArrowLayout(nativeMovement: string | undefined, ar: number): ArrowLayout[] {
  if (!nativeMovement || !(ar > 0)) return [];
  const defs = MOVEMENT_ARROW_SETUP[nativeMovement];
  if (!defs) return [];
  return defs.map((d) => {
    let uw = USED[d.type].w;
    let uh = USED[d.type].h;
    let padX = d.padX;
    let padY = d.padY;

    // A quarter turn swaps both the used fractions and the paddings, since the box rotates with
    // the glyph.
    if (d.rot === 90 || d.rot === 270) {
      [uw, uh] = [uh, uw];
      [padX, padY] = [padY, padX];
    }

    // Padding is expressed in the shorter axis' units, so the longer axis has to give it back.
    if (ar >= 1) padX = padX / ar; else padY = padY * ar;

    // The sprite canvas is square, so its two normalized extents differ by exactly `ar`.
    let w = 1;
    let h = 1;
    if (d.scaling === 'width') {
      w = d.scale; h = w * ar;
    } else if (d.scaling === 'height') {
      h = d.scale; w = h / ar;
    } else if (d.scaling === 'smallest') {
      if (ar >= 1) { h = d.scale; w = h / ar; } else { w = d.scale; h = w * ar; }
    } else {
      if (ar >= 1) { w = d.scale; h = w * ar; } else { h = d.scale; w = h / ar; }
    }

    // Dead margin inside the square canvas, cancelled out by the edge anchors below.
    const padL = w * (1 - uw) / 2;
    const padR = padL;
    const padT = h * (1 - uh) / 2;
    const padB = padT;

    // Unity anchors: normalized, Y measured UP from the bottom.
    let minX = 0;
    let maxX = 1;
    let minY = 0;
    let maxY = 1;
    switch (d.pos) {
      case 'BottomLeft':
        minX = padX - padL; maxX = minX + w;
        minY = padY - padB; maxY = minY + h;
        break;
      case 'TopLeft':
        minX = padX - padL; maxX = minX + w;
        maxY = 1 + padT - padY; minY = maxY - h;
        break;
      case 'TopRight':
        maxX = 1 + padR - padX; minX = maxX - w;
        maxY = 1 + padT - padY; minY = maxY - h;
        break;
      case 'BottomRight':
        maxX = 1 + padR - padX; minX = maxX - w;
        minY = padY - padB; maxY = minY + h;
        break;
      case 'TopMiddle':
        minX = 0.5 - 0.5 * w; maxX = minX + w;
        maxY = 1 + padT - padY; minY = maxY - h;
        break;
      case 'BottomMiddle':
        minX = 0.5 - 0.5 * w; maxX = minX + w;
        minY = padY - padB; maxY = minY + h;
        break;
      case 'LeftMiddle':
        minX = padX - padL; maxX = minX + w;
        minY = 0.5 - 0.5 * h; maxY = minY + h;
        break;
      case 'RightMiddle':
        maxX = 1 + padR - padX; minX = maxX - w;
        minY = 0.5 - 0.5 * h; maxY = minY + h;
        break;
    }

    return {
      sprite: SPRITE[d.type],
      leftPct: minX * 100,
      // Unity's Y grows upward, CSS's downward.
      topPct: (1 - maxY) * 100,
      widthPct: (maxX - minX) * 100,
      heightPct: (maxY - minY) * 100,
      // Unity writes localEulerAngles.z = 0 / -90 / 180 / +90 for None / CW90 / CW180 / CW270.
      // Its +Z is counter-clockwise on a Y-up screen while CSS rotate() is clockwise on a Y-down
      // one; the two inversions cancel, so the degree here is the plain clockwise amount.
      transform: `rotate(${d.rot}deg) scale(${d.flipX ? -1 : 1}, ${d.flipY ? -1 : 1})`,
    };
  });
}

/**
 * 20260930 - The project-aspect SHOT FRAME inside a box of `containerAspect`, as CSS percentages of
 * that box: the rect a picture at the project aspect fills when it is letterboxed (`contain`) into
 * the box, centred by the caller. One function so its two consumers cannot drift apart:
 * ShotMovementOverlay lays its arrows out against it, and ShotPreviewFullscreen crops its still to
 * it (`object-fit: cover`) — Unity centre-crops a static shot's cache tile to the project aspect
 * (ScenePreviewImageManager), but the full-res still that replaces the tile is the UNCROPPED source,
 * so without the frame the fullscreen picture visibly re-framed to the whole source on the swap.
 * A missing or non-positive aspect falls back to 16:9, the loader's default and the overlay's own.
 */
export function projectFrameInBox(
  aspectRatio: number | undefined,
  containerAspect: number = 16 / 9,
): { width: string; height: string } {
  const ar = aspectRatio && aspectRatio > 0 ? aspectRatio : 16 / 9;
  return ar >= containerAspect
    ? { width: '100%', height: `${(containerAspect / ar) * 100}%` }
    : { width: `${(ar / containerAspect) * 100}%`, height: '100%' };
}
